/**
 * Smart NLP question pool for the property page.
 * Questions are categorised into 4 buckets.
 * On each page load we pick ONE question from each bucket per section,
 * ensuring the two sections on the page never show the same question.
 *
 * Each question string is used as the translation key in translations.json.
 * The English text itself is the key — t() resolves it to the current locale.
 */

export interface QuestionBucket {
  id: string;
  label: string;
  questions: string[];
}

export const QUESTION_BUCKETS: QuestionBucket[] = [
  {
    id: "availability",
    label: "Availability & Fit",
    questions: [
      "When is the earliest I can move in?",
      "What room types are available here?",
      "What's the minimum stay duration?",
      "Is this room available for dual occupancy?",
    ],
  },
  {
    id: "money",
    label: "Money",
    questions: [
      "What's the total monthly cost with all fees?",
      "How much is the deposit and when do I pay it?",
      "Can I pay in instalments?",
      "Are bills and Wi-Fi included in the rent?",
    ],
  },
  {
    id: "policies",
    label: "Policies & Risk",
    questions: [
      "What's the cancellation and refund policy?",
      "Will I get a refund if my visa is refused?",
      "Do I need a UK guarantor?",
      "Can I see the tenancy agreement before I pay?",
    ],
  },
  {
    id: "property",
    label: "Place & Property",
    questions: [
      "Which universities are close to this property?",
      "Can I book a viewing first?",
      "Is the kitchen or bathroom shared?",
    ],
  },
];

const TARGET_QUESTION_COUNT = 6;
const AVAILABILITY_PICK_COUNT = 2;
const OTHER_PICK_COUNT = 4;

const shuffle = <T>(items: T[]): T[] => {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const pickRandomItems = (source: string[], count: number): string[] => {
  const pool = [...source];
  const picked: string[] = [];

  while (picked.length < count && pool.length > 0) {
    const index = Math.floor(Math.random() * pool.length);
    picked.push(pool.splice(index, 1)[0]);
  }

  return picked;
};

const getBucketQuestions = (bucketId: string, excludeSet: Set<string>) =>
  QUESTION_BUCKETS.find((bucket) => bucket.id === bucketId)?.questions.filter(
    (question) => !excludeSet.has(question),
  ) ?? [];

/**
 * Picks up to 6 random translation keys for the smart-questions UI.
 *
 * Layout (when the pool allows): [availability, other, other, availability, other, other].
 * Bucket preferences are best-effort — any unfilled slot is backfilled from the remaining
 * global pool so callers always receive a contiguous array with no empty placeholders.
 *
 * Returns fewer than 6 only when fewer than 6 non-excluded questions exist in total
 * (15 in the default pool; the second on-page section excludes the first set's picks).
 *
 * @param excludeQuestions - Questions to exclude (already used by the other section)
 */
export const getRandomQuestions = (excludeQuestions: string[] = []): string[] => {
  const excludeSet = new Set(excludeQuestions);
  let remainingPool = QUESTION_BUCKETS.flatMap((bucket) => bucket.questions).filter(
    (question) => !excludeSet.has(question),
  );

  const pickFromCandidates = (candidates: string[], count: number): string[] => {
    const available = candidates.filter((question) => remainingPool.includes(question));
    const picked = pickRandomItems(available, count);
    remainingPool = remainingPool.filter((question) => !picked.includes(question));
    return picked;
  };

  const availabilityQuestions = getBucketQuestions("availability", excludeSet);
  const selectedAvailability = pickFromCandidates(availabilityQuestions, AVAILABILITY_PICK_COUNT);

  const selectedOthers: string[] = [];
  const otherBuckets = QUESTION_BUCKETS.filter((bucket) => bucket.id !== "availability");

  for (const bucket of otherBuckets) {
    const picked = pickFromCandidates(
      bucket.questions.filter((question) => !selectedOthers.includes(question)),
      1,
    );
    selectedOthers.push(...picked);
  }

  while (selectedOthers.length < OTHER_PICK_COUNT) {
    const remainingPoolSet = new Set(remainingPool);
    const otherCandidates = otherBuckets
      .flatMap((bucket) => bucket.questions)
      .filter((question) => remainingPoolSet.has(question));

    if (otherCandidates.length === 0) {
      break;
    }

    selectedOthers.push(...pickFromCandidates(otherCandidates, 1));
  }

  const shuffledOthers = shuffle(selectedOthers);
  const slots: (string | undefined)[] = [
    selectedAvailability[0],
    shuffledOthers[0],
    shuffledOthers[1],
    selectedAvailability[1],
    shuffledOthers[2],
    shuffledOthers[3],
  ];

  for (let index = 0; index < TARGET_QUESTION_COUNT; index += 1) {
    if (!slots[index] && remainingPool.length > 0) {
      const [backfillQuestion] = pickFromCandidates(remainingPool, 1);
      slots[index] = backfillQuestion;
    }
  }

  return slots.filter((question): question is string => Boolean(question));
};

/**
 * Generates two non-overlapping question sets for the two Smart Question
 * sections on the property page (below About and in FAQ).
 * The second set excludes all questions chosen in the first set.
 */
export const getTwoQuestionSets = (): [string[], string[]] => {
  const firstSet = getRandomQuestions();
  const secondSet = getRandomQuestions(firstSet);
  return [firstSet, secondSet];
};
